/**
 * SemesterPlanTodayCard — surfaces today's tasks from the user's active
 * semester plan(s) at the top of the Dashboard "Today" tab.
 *
 * When the user has no plans → renders nothing (returns null) so the
 * dashboard doesn't pick up empty real-estate. When they DO have a plan
 * but no tasks scheduled for today's weekday → renders a small
 * "rest day" pill that links to the full plan.
 *
 * Uses TanStack Query: data is shared across tabs of the same session
 * and refetches automatically on window focus. 60s staleTime keeps the
 * card cheap.
 */
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { CalendarDays, Check, ArrowRight } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const KIND_PILL = {
  reading:    'bg-blue-50 text-blue-700 border-blue-200',
  practice:   'bg-violet-50 text-violet-700 border-violet-200',
  flashcards: 'bg-amber-50 text-amber-700 border-amber-200',
  review:     'bg-emerald-50 text-emerald-700 border-emerald-200',
  exam_prep:  'bg-rose-50 text-rose-700 border-rose-200',
};

const SemesterPlanTodayCard = () => {
  const qc = useQueryClient();

  const { data, isLoading } = useQuery({
    queryKey: ['semester-plan', 'today'],
    queryFn: async () => (await axios.get(`${API}/semester-plan/today`)).data,
    staleTime: 60_000,
  });

  const toggleTask = useMutation({
    mutationFn: ({ planId, taskId, done }) =>
      axios.post(`${API}/semester-plan/${planId}/tasks/${taskId}/done?done=${done}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['semester-plan', 'today'] }),
  });

  // Stay silent if loading or if the user has zero plans. We don't want
  // an empty placeholder card cluttering the Today tab for users who
  // haven't tried the feature yet.
  if (isLoading) return null;
  const tasks = data?.tasks || [];
  if (tasks.length === 0) return null;

  const completed = tasks.filter((t) => t.task.done).length;

  return (
    <div className="isp-card p-5" data-testid="semester-plan-today-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-[#002FA7]/10 flex items-center justify-center">
            <CalendarDays className="w-5 h-5 text-[#002FA7]" />
          </div>
          <div>
            <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-[#002FA7]">
              From your Semester Plan
            </p>
            <h3 className="text-base font-bold text-[var(--isp-heading)] tracking-tight">
              Today&apos;s study tasks · {completed}/{tasks.length} done
            </h3>
          </div>
        </div>
        <Link to="/semester-plan" className="text-xs text-[var(--isp-muted)] hover:text-[#002FA7] inline-flex items-center gap-1">
          Full plan <ArrowRight className="w-3 h-3" />
        </Link>
      </div>

      <ul className="space-y-2">
        {tasks.slice(0, 4).map((item) => {
          const t = item.task;
          return (
            <li
              key={`${item.plan_id}-${t.id}`}
              className="flex items-start gap-3 p-3 rounded-xl bg-[var(--isp-glass-inner)] border border-[var(--isp-glass-border)]"
              data-testid={`today-plan-task-${t.id}`}
            >
              <button
                onClick={() => toggleTask.mutate({ planId: item.plan_id, taskId: t.id, done: !t.done })}
                className={`mt-0.5 w-5 h-5 rounded-md border flex items-center justify-center transition-colors ${
                  t.done
                    ? 'bg-emerald-500 border-emerald-500 text-white'
                    : 'border-[var(--isp-glass-border)] hover:border-[#002FA7]'
                }`}
                aria-label="Toggle done"
                data-testid={`today-plan-toggle-${t.id}`}
              >
                {t.done && <Check className="w-3 h-3" />}
              </button>
              <div className="flex-1 min-w-0">
                <p className={`text-sm font-medium leading-snug ${t.done ? 'line-through text-[var(--isp-muted)]' : 'text-[var(--isp-heading)]'}`}>
                  {t.title}
                </p>
                <div className="flex items-center gap-2 mt-1 flex-wrap">
                  <span className="text-[10px] text-[var(--isp-muted)]">{item.subject_name}</span>
                  <span className="text-[10px] text-[var(--isp-muted)]">·  {t.minutes} min</span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${KIND_PILL[t.kind] || 'bg-slate-50 text-slate-700 border-slate-200'}`}>
                    {t.kind?.replace('_', ' ')}
                  </span>
                </div>
              </div>
            </li>
          );
        })}
      </ul>
      {tasks.length > 4 && (
        <p className="mt-3 text-[11px] text-[var(--isp-muted)] text-center">
          +{tasks.length - 4} more — open full plan
        </p>
      )}
    </div>
  );
};

export default SemesterPlanTodayCard;
